import Vue from "vue";
import Router from "vue-router";
import { slugifyForUrl } from "@/utils/slug";
import { getServiceSeo, pageSeo } from "@/utils/seo";
import HomePage from "@/views/HomeView.vue";
import NotFound from "@/views/NotFound.vue";
import About from "@/views/AboutUsView.vue";
import Services from "@/views/ServicesView.vue";
import ServicesDetails from "@/views/ServicesDetailedView.vue";
import Contact from "@/views/ContactFormView.vue";
import Blog from "@/views/BlogView.vue";
import News from "@/views/NewsView.vue";
import BlogDetails from "@/views/BlogDetailedView.vue";
import PrivacyPolicyUs from "@/views/PrivacyPolicyUs.vue";
import PrivacyPolicyIndia from "@/views/PrivacyPolicyIndia.vue";
import TermsAndConditions from "@/views/TermsAndConditions.vue";
import TermsAndConditionsUsa from "@/views/TermsAndConditionsUsa.vue";
import PartnerPrivacyUSA from "@/views/PartnerPrivacyUSA.vue";
import CategoryDetailedView from "@/views/CategoryDetailedView.vue";
import CategoryServicesView from "@/views/CategoryServicesView.vue";
import AddBookings from "@/views/AddBookings.vue";
import MyBookingsView from "@/views/MyBookingsView.vue";
import BookingDetailsView from "@/views/BookingDetailsView.vue";
import PartnerPrivacyIndia from "@/views/PartnerPrivacyIndia.vue";
import PartnerTermsIndia from "@/views/PartnerTermsIndia.vue";
import PartertermsUSA from "@/views/PartertermsUSA.vue";
Vue.use(Router);

const LOCATION_CHECK_INTERVAL_MS = 10 * 60 * 1000;

function shouldRunLocationCheck() {
  const lastCheckAt = Number(sessionStorage.getItem("lastLocationCheckAt") || 0);
  return !lastCheckAt || Date.now() - lastCheckAt >= LOCATION_CHECK_INTERVAL_MS;
}

function clearAuthSession() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("mobile_number");
  localStorage.removeItem("is_registered");
  localStorage.removeItem("authRegion");

  if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === "function") {
    Vue.prototype.$bus.$emit("auth-change");
  }
}

function getRouteRegion(path) {
  return path && path.startsWith("/us") ? "US" : "IN";
}

// Function to detect and store user location
async function detectAndStoreLocation() {
  const previousCountry = localStorage.getItem("userCountry");
  const hadAuthSession = !!localStorage.getItem("access_token");

  try {
    const response = await fetch("https://ipapi.co/json/");
    if (!response.ok) {
      throw new Error(`Location API failed with status ${response.status}`);
    }

    const data = await response.json();
    const detectedCountry = data && data.country_code;

    if (!detectedCountry) {
      throw new Error("Location API did not return country_code");
    }

    const isUS = detectedCountry === "US";
    const countryChanged =
      !!previousCountry && previousCountry !== detectedCountry;

    if (countryChanged && hadAuthSession) {
      clearAuthSession();
    }

    localStorage.setItem("userCountry", detectedCountry);
    localStorage.setItem("isUS", isUS ? "true" : "false");

    return {
      isUS,
      didLogout: countryChanged && hadAuthSession,
    };
  } catch (error) {
    // Keep existing values to avoid accidental logout/region switches.
    console.error("Error detecting location:", error);
    const existingCountry = localStorage.getItem("userCountry");
    const existingIsUS = localStorage.getItem("isUS");
    const isUSFallback =
      existingCountry === "US" || existingIsUS === "true";

    return {
      isUS: isUSFallback,
      didLogout: false,
    };
  }
}

const router = new Router({
  mode: "history",
  routes: [
    // Default routes (non-US)
    {
      path: "/",
      name: "Home",
      component: HomePage,
    },
    {
      path: "/about",
      name: "About",
      component: About,
      meta: { seo: pageSeo.about },
    },
    {
      path: "/services",
      name: "Services",
      component: Services,
      meta: { seo: pageSeo.services },
    },
    {
      path: "/services/:serviceName",
      name: "ServicesDetails",
      component: ServicesDetails,
      props: true,
      meta: { seo: (to) => getServiceSeo(to.params.serviceName) },
    },
    {
      path: "/service/:serviceSlug",
      name: "ServiceDetails",
      component: CategoryDetailedView,
      props: true,
      meta: { seo: (to) => getServiceSeo(to.params.serviceSlug) },
    },
    {
      path: "/CategoryDetailedView/:serviceId",
      redirect: (to) => ({
        name: "ServiceDetails",
        params: { serviceSlug: to.params.serviceId },
      }),
    },
    {
      path: "/category/:categoryId/:categoryName",
      redirect: (to) => ({
        name: "CategoryServices",
        params: { categorySlug: slugifyForUrl(to.params.categoryName || "") },
      }),
    },
    {
      path: "/category/:categorySlug",
      name: "CategoryServices",
      component: CategoryServicesView,
      props: true,
      meta: { seo: (to) => getServiceSeo(to.params.categorySlug) },
    },

    {
      path: "/contact",
      name: "Contact",
      component: Contact,
    },
    {
      path: "/news",
      name: "News",
      component: News,
      meta: { seo: pageSeo.news },
    },

    // US routes (location-specific)
    {
      path: "/us",
      name: "HomeUS",
      component: HomePage,
      meta: { location: "us" },
    },
    {
      path: "/us/about",
      name: "AboutUS",
      component: About,
      meta: { location: "us", seo: pageSeo.about },
    },
    {
      path: "/us/services",
      name: "ServicesUS",
      component: Services,
      meta: { location: "us", seo: pageSeo.services },
    },
    {
      path: "/us/services/:serviceName",
      name: "ServicesDetailsUS",
      component: ServicesDetails,
      props: true,
      meta: {
        location: "us",
        seo: (to) => getServiceSeo(to.params.serviceName),
      },
    },
    {
      path: "/us/category/:categoryId/:categoryName",
      redirect: (to) => ({
        name: "CategoryServicesUS",
        params: { categorySlug: slugifyForUrl(to.params.categoryName || "") },
      }),
    },
    {
      path: "/us/category/:categorySlug",
      name: "CategoryServicesUS",
      component: CategoryServicesView,
      props: true,
      meta: {
        location: "us",
        seo: (to) => getServiceSeo(to.params.categorySlug),
      },
    },
    {
      path: "/us/contact",
      name: "ContactUS",
      component: Contact,
      meta: { location: "us" },
    },
    {
      path: "/us/news",
      name: "NewsUS",
      component: News,
      meta: { location: "us", seo: pageSeo.news },
    },
    {
      path: "/privacy-policy",
      name: "privacy-policy",
      component: PrivacyPolicyIndia,
    },
    {
      path: "/privacy-policy/",
      redirect: { name: "privacy-policy" },
    },
    {
      path: "/terms-conditions",
      name: "terms-conditions",
      component: TermsAndConditions,
    },
    {
      path: "/terms-conditions/",
      redirect: { name: "terms-conditions" },
    },
    {
      path: "/privacy-policy-of-us",
      name: "privacy-policy-of-us",
      component: PrivacyPolicyUs,
    },
    {
      path: "/privacy-policy-of-us/",
      redirect: { name: "privacy-policy-of-us" },
    },
    {
      path: "/terms-and-conditions",
      name: "terms-and-conditions",
      component: TermsAndConditionsUsa,
    },
    {
      path: "/terms-and-conditions/",
      redirect: { name: "terms-and-conditions" },
    },
    {
      path: "/AddBookings/:serviceTypeId/:serviceName?",
      name: "AddBookings",
      component: AddBookings,
      props: true,
    },
    {
      path: "/bookings",
      name: "MyBookings",
      component: MyBookingsView,
    },
    {
      path: "/booking/:bookingId",
      name: "BookingDetails",
      component: BookingDetailsView,
      props: true,
    },

    {
      path: "/blog",
      name: "Blog",
      component: Blog,
      meta: { seo: pageSeo.blog },
    },
    {
      path: "/blog/:blogTitle",
      name: "BlogDetails",
      component: BlogDetails,
      props: true,
    },

    // Bangalore routes (if you want to keep this pattern)
    {
      path: "/bangalore",
      name: "HomeBangalore",
      component: HomePage,
      meta: { location: "bangalore" },
    },

    {
      path: "/service-provider-privacy-policy-usa",
      name: "service-provider-privacy-policy-usa",
      component: PartnerPrivacyUSA,
    },
    {
      path: "/service-provider-privacy-policy-usa/",
      redirect: { name: "service-provider-privacy-policy-usa" },
    },
    {
      path: "/service-provider-privacy-policy-ind",
      name: "service-provider-privacy-policy-ind",
      component: PartnerPrivacyIndia,
    },
    {
      path: "/service-provider-privacy-policy-ind/",
      redirect: { name: "service-provider-privacy-policy-ind" },
    },
    {
      path: "/service-provider-terms-and-conditions",
      name: "service-provider-terms-and-conditions",
      component: PartertermsUSA,
    },
    {
      path: "/service-provider-terms-and-conditions/",
      redirect: { name: "service-provider-terms-and-conditions" },
    },
    {
      path: "/service-provider-terms-and-conditions-ind",
      name: "service-provider-terms-and-conditions-ind",
      component: PartnerTermsIndia,
    },
    {
      path: "/service-provider-terms-and-conditions-ind/",
      redirect: { name: "service-provider-terms-and-conditions-ind" },
    },

    // 404 - must be last
    {
      path: "*",
      name: "NotFound",
      component: NotFound,
      meta: { hideLayout: true },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { x: 0, y: 0, behavior: "smooth" };
  },
});

// Navigation guard to redirect US users
router.beforeEach(async (to, from, next) => {
  const toRegion = getRouteRegion(to.path);
  const hasAuthSession = !!localStorage.getItem("access_token");
  const authRegion = localStorage.getItem("authRegion");

  if (hasAuthSession && authRegion && authRegion !== toRegion) {
    clearAuthSession();
    localStorage.setItem("authRegion", toRegion);
    next();
    return;
  }

  if (hasAuthSession && !authRegion) {
    localStorage.setItem("authRegion", toRegion);
  }

  if (shouldRunLocationCheck()) {
    sessionStorage.setItem("lastLocationCheckAt", String(Date.now()));
    const locationResult = await detectAndStoreLocation();
    const inUSA = locationResult.isUS;

    if (locationResult.didLogout) {
      next(inUSA ? "/us" : "/");
      return;
    }

    if (to.path === "/" && inUSA) {
      next("/us");
      return;
    } else {
      next();
      return;
    }
  }

  localStorage.setItem("authRegion", toRegion);
  next();
});

export default router;
